import React, { useRef, useState, useEffect } from 'react';
import {
  X,
  Check,
  Clock,
  Calculator,
  Receipt,
  Bell,
  FileSpreadsheet,
  BarChart3,
  Zap,
} from 'lucide-react';
import useScrollVisible from '../../hooks/useScrollVisible';

function AnimatedValue({ end, prefix = '', suffix = '', isVisible }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 1500;
    let startTime = null;
    let raf;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [end, isVisible]);

  return (
    <span>
      {prefix}
      {count}
      {suffix}
    </span>
  );
}

export default function BeforeAfterComparison() {
  const sectionRef = useRef(null);
  const isVisible = useScrollVisible(sectionRef);

  const beforeItems = [
    { icon: FileSpreadsheet, text: 'Scattered spreadsheets for every project' },
    { icon: Calculator, text: 'Manual calculations prone to errors' },
    { icon: Receipt, text: 'Lost receipts and no expense tracking' },
    { icon: Clock, text: 'Missed follow-ups on promising leads' },
  ];

  const afterItems = [
    { icon: BarChart3, text: 'One unified dashboard for everything' },
    { icon: Zap, text: 'Auto-calculated ROI, profit, and MAO' },
    { icon: Receipt, text: 'AI-scanned receipts, auto-categorized' },
    { icon: Bell, text: 'Pipeline alerts so no deal slips' },
  ];

  return (
    <section className="py-14 px-6" ref={sectionRef}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-2xl md:text-3xl font-bold max-w-2xl mx-auto leading-relaxed">
            <span className="text-white">Most flippers lose money not from bad deals — </span>
            <br className="hidden md:inline" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400">
              but from not tracking what matters fast enough.
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-0 relative">
          {/* Before */}
          <div className="bg-red-500/[0.04] border border-red-500/[0.12] md:border-r-0 rounded-2xl md:rounded-r-none p-6 relative">
            {/* Red tint overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/[0.03] to-transparent rounded-2xl md:rounded-r-none pointer-events-none" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center">
                  <X size={16} className="text-red-400" />
                </div>
                <span className="text-sm font-bold text-red-400 uppercase tracking-wider">
                  Before FlipperPro
                </span>
              </div>

              {/* Messy spreadsheet mockup */}
              <div className="bg-slate-800/60 rounded-xl p-3 mb-5 border border-white/[0.05] opacity-60">
                <div className="grid grid-cols-5 gap-px">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <div key={i} className="bg-slate-700/40 h-4 rounded-sm text-center">
                      <span className="text-[6px] text-slate-500">
                        {
                          [
                            '$185K',
                            'ARV?',
                            '#REF!',
                            '???',
                            '$42K',
                            'Est.',
                            'TBD',
                            '$295',
                            'need',
                            'check',
                          ][i % 10]
                        }
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-[8px] text-slate-600 mt-2 text-center italic">
                  My_Flip_Budget_v4_FINAL_FINAL.xlsx
                </p>
              </div>

              <div className="space-y-3">
                {beforeItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                      <X size={12} className="text-red-400" />
                    </div>
                    <span className="text-sm text-slate-400">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* VS Divider (desktop only) */}
          <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
            <div className="w-12 h-12 rounded-full bg-slate-800 border-2 border-violet-500/40 flex items-center justify-center shadow-lg shadow-violet-500/15">
              <span className="text-xs font-extrabold text-violet-400">VS</span>
            </div>
          </div>

          {/* After */}
          <div className="bg-emerald-500/[0.04] border border-emerald-500/[0.12] md:border-l-0 rounded-2xl md:rounded-l-none p-6 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/[0.03] to-transparent rounded-2xl md:rounded-l-none pointer-events-none" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <Check size={16} className="text-emerald-400" />
                </div>
                <span className="text-sm font-bold text-emerald-400 uppercase tracking-wider">
                  After FlipperPro
                </span>
              </div>

              {/* Clean dashboard mockup */}
              <div className="bg-slate-800/60 rounded-xl p-3 mb-5 border border-emerald-500/10">
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div className="bg-emerald-500/10 rounded-md px-2 py-1.5 text-center">
                    <p className="text-[7px] text-slate-500">Profit</p>
                    <p className="text-xs font-bold text-emerald-400">
                      $<AnimatedValue end={47} isVisible={isVisible} />K
                    </p>
                  </div>
                  <div className="bg-blue-500/10 rounded-md px-2 py-1.5 text-center">
                    <p className="text-[7px] text-slate-500">ROI</p>
                    <p className="text-xs font-bold text-blue-400">
                      <AnimatedValue end={22} isVisible={isVisible} />%
                    </p>
                  </div>
                  <div className="bg-violet-500/10 rounded-md px-2 py-1.5 text-center">
                    <p className="text-[7px] text-slate-500">Budget</p>
                    <p className="text-xs font-bold text-violet-400">
                      <AnimatedValue end={72} isVisible={isVisible} />%
                    </p>
                  </div>
                </div>
                <div className="h-6 flex items-end gap-1">
                  {[30, 45, 55, 65, 50, 70, 80].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-gradient-to-t from-emerald-500/50 to-blue-500/30 rounded-t-sm"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {afterItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                      <Check size={12} className="text-emerald-400" />
                    </div>
                    <span className="text-sm text-slate-300">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Impact stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {[
            {
              value: 10,
              suffix: '+ hrs/week',
              label: 'Saved on Admin',
              prefix: '',
              color: 'text-emerald-400',
            },
            {
              value: 3,
              suffix: 'x faster',
              label: 'Deal Analysis',
              prefix: '',
              color: 'text-blue-400',
            },
            {
              value: 15,
              suffix: 'K avg',
              label: 'Budget Savings',
              prefix: '$',
              color: 'text-violet-400',
            },
          ].map((stat, i) => (
            <div
              key={i}
              className="text-center bg-white/[0.03] border border-white/[0.07] rounded-xl py-4 px-3"
            >
              <p className={`text-2xl md:text-3xl font-extrabold ${stat.color} mb-1`}>
                <AnimatedValue
                  end={stat.value}
                  prefix={stat.prefix}
                  suffix={stat.suffix}
                  isVisible={isVisible}
                />
              </p>
              <p className="text-xs text-slate-500 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
